<template>
  <div class="container">
    <donut-page-title
      title="Points settings"
      subtitle="Tailor your recognition points to align with your company culture."
    />
    <donut-form method="put" action="/points/config" :record="pointsConfig">
      <div class="w-100p f-je m-tx1">
        <button
          :class="{disabled: formIsInvalid, loading: state.formSubmitted}"
        >
          Save
        </button>
      </div>
      <section class="m-bx4">
        <h2 class="t t-lg">What are recognition points?</h2>
        <p class="t">
          Employees can earn recognition points by completing Donut Intros,
          celebrating milestones such as work anniversaries or birthdays, and
          receiving {{ $i18n.t('shoutouts.shoutout.other') }} from colleagues. Recognition points can be
          redeemed by customized rewards or gift cards. Customize rewards in the
          <a class="link c-p" href="/rewards/settings">Store settings</a>.
        </p>
      </section>
      <!-- Emoji -->
      <donut-form-item label="Emoji" :left-align="true">
        <template #underLabel>
          <div v-emoji="emojiDesc" class="description" />
          <points-slack-previews-channel-shoutout-message class="m-tx2" />
        </template>
        <div class="f-h f-je">
          <div class="f-v">
            <donut-form-input
              :for="pointsConfig.emoji"
              name="emoji"
              :validator="presenceValidator('An emoji')"
            >
              <div class="f-je">
                <donut-selector-emoji
                  v-model="emojiSelection"
                  form-name="points_config[emoji]"
                  :categories="[
                    'foods',
                    'activity',
                    'nature',
                    'places',
                    'objects',
                    'symbols',
                    'flags',
                    'people',
                    'customSlack',
                  ]"
                  :paywall="paywall('emoji', emojiFeaturePaywallTitle, 'points-settings-emoji')"
                />
              </div>
              <div class="f-v">
                <div
                  v-for="(alias, i) in emojiAliases"
                  :key="`emoji-alias-${alias}`"
                  :class="`f-je ${i === 0 ? 'm-t' : 'm-t2'}`"
                >
                  <div class="t-sm gray">:{{ alias }}:</div>
                </div>
                <a
                  v-if="reauthUrlForCustomEmoji && !upgradeRequired('emoji')"
                  class="t-sm link m-tx1"
                  @click="openPermissionsModal"
                >Use a custom emoji from your Slack workspace</a>
              </div>
            </donut-form-input>
          </div>
        </div>
      </donut-form-item>
      <div class="separator m-vx2" />
      <!-- Points -->
      <donut-form-item
        :label="pointsLabel"
        :description="pointsDesc"
        :left-align="true"
      >
        <template #underLabel>
          <points-slack-previews-channel-shoutout-received-dm class="m-tx2" />
        </template>
        <donut-form-input
          class="f-ae"
          :for="pointsConfig.points_name"
          name="points"
          :validator="presenceValidator('A name for points')"
        >
          <div class="f-h">
            <div class="f-v f-ae">
              <div class="w-x18">
                <donut-input-text
                  ref="pointsInputEl"
                  v-model="pointsSelection"
                  :ellipsis="true"
                  placeholder="Name for points..."
                  name="points_config[points_name]"
                  :paywall="paywall('points_name', 'customize the name for points', 'points-settings-name')"
                />
              </div>
              <div class="t gray m-tx1">{{ pointPluralizerCopy }}</div>
            </div>
          </div>
        </donut-form-input>
      </donut-form-item>
    </donut-form>
  </div>
</template>

<script setup>
  import { ref, computed, inject, watch, onMounted } from 'vue';
  import { useStore } from 'vuex';
  import useShoutoutsPointsAndEmojis from 'composables/shoutouts/points-and-emojis';
  import Scroll from 'lib/scroll';

  const { state, getters, commit, dispatch } = useStore();
  const $i18n = inject('$i18n');

  const pointsConfig = computed(() => state.pointsConfig.config);
  const pointsInputEl = ref(null);

  const emojiFeaturePaywallTitle = 'customize the emoji used for points';

  const formIsInvalid = computed(() => {
    if (!state.errors) return false;
    return Object.keys(state.errors).some((errorKey) => state.errors[errorKey]);
  });

  // Paywalls
  const paywall = (feature, featureTitleText, source) => ({ data: pointsConfig.value.upgrade_data, feature, featureTitleText, source, programKey: 'shoutouts' });
  const upgradeRequired = (feature) => getters.upgradeRequired(paywall(feature));

  // Slack Auth
  const reauthUrlForCustomEmoji = computed(
    () => state.reauthing.slackAuth.custom_emoji?.path,
  );
  const openPermissionsModal = () => {
    dispatch('reauthing/openPermissionsModal', {
      contextName: 'custom_emoji',
      source: 'Points Config Settings: Custom Emoji',
      missingScope: 'emoji_read',
    });
  };

  const pointsName = computed(() => pointsConfig.value.points_name);
  const { cacheValueAtFullPath, activelyTyping, emojiDesc, pointWords } = useShoutoutsPointsAndEmojis(pointsName);

  // Emoji
  const emojiAliases = ref([]);
  const emojiSelection = computed({
    get() { return pointsConfig.value.emoji; },
    set(emoji) {
      pointsConfig.value.emoji = emoji.id;
      emojiAliases.value = [emoji.id].concat(emoji.aliases || []);
      if (!emoji.fromMount) {
        pointsConfig.value.points_name = emoji.name;
        Scroll.addHighlightToDisplayElement(pointsInputEl.value.$el, 3000);
      }
    },
  });

  // Points
  const pointsLabel = computed(
      () => `Name for ${_.capitalize($i18n.t('shoutouts.point', { count: 2 }))}`,
  );
  const pointsDesc = computed(
      () => 'You can stick with the name of the emoji or choose a custom name that fits your culture.\n\n'
          + `This name will appear in messages when someone sends or receives a ${$i18n.t('shoutouts.shoutout', { count: 1 })}.`,
  );
  const pointsSelection = computed({
    get() { return pointsConfig.value.points_name; },
    set(value) { commit('pointsConfig/updateConfigKey', { key: 'points_name', value }); },
  });

  // Copy
  const loadingPhraseIdx = ref(0);
  const loadingPhraseOptions = ['.', '..', '...', '....', '.....'];
  const loadingPhrase = computed(
    () => loadingPhraseOptions[loadingPhraseIdx.value],
  );
  const pointPluralizerCopy = computed(() => {
    const data = cacheValueAtFullPath.value;
    if (activelyTyping.value) return loadingPhrase.value;
    if (!data || data.status === 'complete') {
      return `e.g. You received 1 ${pointWords.value.singular}. Dan received 3 ${pointWords.value.plural}.`;
    } else if (data?.status === 'pending') {
      return loadingPhrase.value;
    } else if (data?.status === 'error') {
      return 'Invalid noun';
    }
  });
  const beginLoadingPhraseCycle = () => {
    setInterval(() => {
      loadingPhraseIdx.value = (loadingPhraseIdx.value + 1) % loadingPhraseOptions.length;
    }, 300);
  };

  // Lifecycle Hooks
  onMounted(() => {
    beginLoadingPhraseCycle();
  });
</script>
